/**
 * Portable formatters for traffic deltas (no React/Redux).
 */

/**
 * @param {number} seconds
 * @returns {string} e.g. "+2m 15s", "-45s", "0s"
 */
export function formatSignedDuration(seconds) {
  if (seconds == null || Number.isNaN(seconds)) {
    return '—'
  }
  const sign = seconds > 0 ? '+' : seconds < 0 ? '-' : ''
  const abs = Math.abs(Math.round(seconds))
  const hours = Math.floor(abs / 3600)
  const minutes = Math.floor((abs % 3600) / 60)
  const secs = abs % 60
  const parts = []
  if (hours > 0) {
    parts.push(`${hours}h`)
  }
  if (minutes > 0) {
    parts.push(`${minutes}m`)
  }
  if (secs > 0 || parts.length === 0) {
    parts.push(`${secs}s`)
  }
  return `${sign}${parts.join(' ')}`
}

/**
 * @param {number} deltaSec
 * @param {number} baselineSec
 * @returns {number|null} percent change relative to baseline
 */
export function percentDelta(deltaSec, baselineSec) {
  if (
    deltaSec == null ||
    baselineSec == null ||
    Number.isNaN(deltaSec) ||
    Number.isNaN(baselineSec) ||
    baselineSec === 0
  ) {
    return null
  }
  return (deltaSec / baselineSec) * 100
}

/**
 * @param {number|null} pct
 * @param {number} [digits=1]
 * @returns {string}
 */
export function formatPercent(pct, digits = 1) {
  if (pct == null || Number.isNaN(pct)) {
    return '—'
  }
  const sign = pct > 0 ? '+' : ''
  return `${sign}${pct.toFixed(digits)}%`
}

export function buildTimeDelta(valhallaSec, trafficSec) {
  const timeSec =
    valhallaSec != null && trafficSec != null ? trafficSec - valhallaSec : null
  const timePct = percentDelta(timeSec, valhallaSec)
  return {
    timeSec,
    timeHuman: formatSignedDuration(timeSec),
    timePct,
    timePctHuman: formatPercent(timePct),
  }
}

/** Mapbox annotation speed is m/s. */
export function mpsToMph(mps) {
  if (mps == null || Number.isNaN(mps)) {
    return null
  }
  return mps * 2.23693629
}

/**
 * Mapbox maxspeed annotation → mph.
 * @param {{ speed?: number, unit?: string, unknown?: boolean }|null} maxspeed
 * @returns {number|null}
 */
export function maxspeedToMph(maxspeed) {
  if (!maxspeed || typeof maxspeed !== 'object' || maxspeed.unknown) {
    return null
  }
  if (maxspeed.speed == null || Number.isNaN(maxspeed.speed)) {
    return null
  }
  const unit = String(maxspeed.unit || 'km/h').toLowerCase()
  if (unit === 'mph' || unit === 'mi/h' || unit === 'miles/hour') {
    return maxspeed.speed
  }
  // Mapbox default is km/h
  return maxspeed.speed * 0.621371192
}
